import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import SaveIcon from "@mui/icons-material/Save";
import ShortcutIcon from "@mui/icons-material/Shortcut";
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    GridLegacy as Grid,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import update from "immutability-helper";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    createEventObject,
    deleteEventObject,
    getEventById,
    getEventObjects,
    initEventObjects,
    reorderEventObjects,
    updateEventObject,
} from "../../../api/event";
import { EventBlockCard } from "../../../components/EventBlockCard";
import { SiteStateContext } from "../../../contexts/SiteStateContext";
import { fixSiteState } from "../../../util/site_state";
import AdminLayout from "../AdminLayout";

const AdminEventConfig = () => {
    const params = useParams();
    const { setSiteState } = useContext(SiteStateContext);
    const [event, setEvent] = useState<EventItem>();
    const [blocks, setBlocks] = useState<EventObject[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newBlockTitle, setNewBlockTitle] = useState("");
    const [newBlockType, setNewBlockType] = useState<number>(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (setSiteState) {
            setSiteState(
                fixSiteState({
                    title: "Configure - Events - Admin",
                    navHighlight: "/admin/events",
                    adminPage: true,
                    actions: [
                        {
                            icon: EventIcon,
                            text: "All Events",
                            onClick: () => {
                                navigate(`/admin/events`);
                            },
                        },
                        {
                            icon: ShortcutIcon,
                            text: "Jump to",
                            onClick: () => {
                                navigate(`/event/${event?.url}`);
                            },
                        },
                    ],
                })
            );
        }
    }, [setSiteState, event, navigate]);

    const loadBlocks = useCallback(async (eventId: string) => {
        try {
            const objects = await getEventObjects(eventId);
            if (objects.length === 0) {
                // Initialize default blocks if none exist
                await initEventObjects(eventId);
                const initializedObjects = await getEventObjects(eventId);
                setBlocks(initializedObjects.sort((a, b) => a.order - b.order));
            } else {
                setBlocks(objects.sort((a, b) => a.order - b.order));
            }
        } catch (err) {
            console.warn(err);
        }
    }, []);

    useEffect(() => {
        if (!params.id) return;
        setLoading(true);
        getEventById(params.id)
            .then((ev) => {
                setEvent(ev);
                return loadBlocks(ev.id);
            })
            .then(() => {
                setLoading(false);
            })
            .catch((err) => {
                console.warn(err);
                setLoading(false);
            });
    }, [params, loadBlocks]);

    const moveBlock = useCallback((dragIndex: number, hoverIndex: number) => {
        setBlocks((prevBlocks) =>
            update(prevBlocks, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, prevBlocks[dragIndex]],
                ],
            })
        );
        setHasChanges(true);
    }, []);

    const updateBlock = useCallback((updatedBlock: EventObject) => {
        setBlocks((prevBlocks) =>
            prevBlocks.map((b) => (b.id === updatedBlock.id ? updatedBlock : b))
        );
        setHasChanges(true);
    }, []);

    const deleteBlock = useCallback((blockId: string) => {
        setBlocks((prevBlocks) => prevBlocks.filter((b) => b.id !== blockId));
        setHasChanges(true);
    }, []);

    const handleSave = async () => {
        if (!event) return;
        setSaving(true);
        try {
            // First, update the order
            const newOrder = blocks.map((b) => b.id);
            await reorderEventObjects(event.id, newOrder);

            // Then update each block (excluding order field to avoid overwriting)
            for (const block of blocks) {
                const { order, ...blockWithoutOrder } = block;
                await updateEventObject(block.id, blockWithoutOrder);
            }

            setHasChanges(false);
        } catch (err) {
            console.warn(err);
        }
        setSaving(false);
    };

    const handleAddBlock = async () => {
        if (!event || !newBlockTitle.trim()) return;
        try {
            const newBlock = await createEventObject(event.id, {
                title: newBlockTitle,
                type: newBlockType,
                required: false,
                enabled: true,
            });
            setBlocks((prev) => [...prev, newBlock]);
            setNewBlockTitle("");
            setNewBlockType(0);
            setAddDialogOpen(false);
            setHasChanges(true);
        } catch (err) {
            console.warn(err);
        }
    };

    const renderBlock = useCallback(
        (block: EventObject, index: number) => {
            return (
                <EventBlockCard
                    key={block.id}
                    id={block.id}
                    index={index}
                    block={block}
                    moveBlock={moveBlock}
                    updateBlock={updateBlock}
                    deleteBlock={() => {
                        deleteEventObject(block.id)
                            .then(() => deleteBlock(block.id))
                            .catch((err) => console.warn(err));
                    }}
                    disabled={event?.archived}
                />
            );
        },
        [moveBlock, updateBlock, deleteBlock, event?.archived]
    );

    return (
        <AdminLayout>
            <Container sx={{ m: 0 }} maxWidth={false}>
                <Typography variant="h2" sx={{ mt: 2, mb: 2 }}>
                    Configure Event Fields
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Drag and drop to reorder fields. Enable/disable fields using the checkbox. Right-click to delete.
                </Typography>

                {loading ? (
                    <CircularProgress />
                ) : event ? (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={3}>
                            <ButtonGroup variant="outlined" fullWidth orientation="vertical">
                                <Button
                                    onClick={() => {
                                        navigate(`/admin/events/${event.id}/edit`);
                                    }}
                                >
                                    Return to Edit
                                </Button>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddDialogOpen(true)}
                                    disabled={event.archived}
                                >
                                    Add Field
                                </Button>
                            </ButtonGroup>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    disabled={event.archived || saving || !hasChanges}
                                >
                                    {saving ? "Saving..." : "Save Changes"}
                                </Button>
                            </Box>
                            {hasChanges && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    You have unsaved changes
                                </Alert>
                            )}
                        </Grid>
                        <Grid item xs={12} md={9}>
                            {blocks.length === 0 ? (
                                <Typography color="text.secondary">
                                    No fields configured. Click "Add Field" to create one.
                                </Typography>
                            ) : (
                                blocks.map((block, index) => renderBlock(block, index))
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Typography>Event not found</Typography>
                )}

                {/* Add Block Dialog */}
                <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
                    <DialogTitle>Add New Field</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Field Title"
                            fullWidth
                            variant="standard"
                            value={newBlockTitle}
                            onChange={(e) => setNewBlockTitle(e.target.value)}
                        />
                        <Select
                            value={newBlockType}
                            onChange={(e) => setNewBlockType(Number(e.target.value))}
                            fullWidth
                            variant="standard"
                            sx={{ mt: 2 }}
                        >
                            <MenuItem value={0}>Text</MenuItem>
                            <MenuItem value={1}>Number</MenuItem>
                            <MenuItem value={5}>Dropdown</MenuItem>
                            <MenuItem value={4}>Checkbox</MenuItem>
                        </Select>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleAddBlock} disabled={!newBlockTitle.trim()}>
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </AdminLayout>
    );
};

export default AdminEventConfig;
